import React, { FC, PropsWithChildren, useState } from 'react';
import { Accept, DropzoneOptions, useDropzone } from 'react-dropzone';
import styled from 'styled-components';
import ThemeColors from '../common/theme/colors';
import { TranslateFunc } from '../common/types';
import { border, duration, fontWeight, spacing } from '../common/theme/constants';
import KitButton from './KitButton';

export type KitDropzoneOptions = DropzoneOptions;

const getColor = (props) => {
  if (props.isDragAccept) {
    return ThemeColors.green_50;
  }
  if (props.isDragReject) {
    return ThemeColors.red_50;
  }
  if (props.isDragActive) {
    return ThemeColors.blue_55;
  }
  return ThemeColors.gray_20;
};

const Container = styled.div`
  cursor: pointer;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: ${spacing.absolute.m}px;
  border-width: ${border.width}px;
  border-radius: ${border.radius}px;
  border-color: ${(props) => getColor(props)};
  border-style: dashed;
  background-color: ${({ theme }) => theme.page.body.background};
  color: ${({ theme }) => theme.page.body.text};
  outline: none;
  transition: border ${duration.short}s ease-in-out;
  margin-bottom: ${spacing.absolute.m}px;
  max-width: 350px;
  height: 205px;
  &:focus {
    border-color: ${({ theme }) => theme.focus};
  }
`;

const Text = styled.div`
  padding: ${spacing.absolute.s}px;
  font-weight: ${fontWeight.bold};
`;

const ButtonWrapper = styled.div`
  margin-top: ${spacing.absolute.s}px;
`;

export interface KitDropzoneLabels {
  button?: string;
  text?: string;
  subtext?: string;
}
export interface KitDropzoneProps {
  url?: string;
  fileTypes?: Accept;
  t: TranslateFunc;
  onUploadError?: (error: unknown) => void;
  onUploadSuccess?: (response: unknown) => void;
  onDrop?: (files: File[]) => void;
  options?: KitDropzoneOptions;
  labels?: KitDropzoneLabels;
  style?: { [field: string]: string | number };
  fetchOptions?: Record<string, unknown>;
  formKeyName: string;
  formImageName?: string;
}

const KitDropzone: FC<KitDropzoneProps> = ({
  url,
  fileTypes,
  t,
  onUploadError,
  onUploadSuccess,
  onDrop,
  options,
  labels,
  style,
  fetchOptions,
  formKeyName,
  formImageName,
  children,
}: PropsWithChildren<KitDropzoneProps>) => {
  const [filesForUpload, setFilesForUpload] = useState([]);

  const submitForm = (event?) => {
    event?.preventDefault();

    const dataArray = new FormData();

    if (formImageName) {
      dataArray.append(formKeyName, filesForUpload as unknown as Blob, formImageName);
    } else {
      dataArray.append(formKeyName, filesForUpload as unknown as Blob);
    }

    fetch(url, {
      method: 'POST',
      body: dataArray,
      ...fetchOptions,
    })
      .then((response) => {
        onUploadSuccess(response);
      })
      .catch((error: unknown) => {
        onUploadError(error);
      });
  };

  const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject } = useDropzone({
    accept: fileTypes,
    onDrop: (acceptedFiles) => {
      if (!acceptedFiles.length) {
        return;
      }
      if (onDrop) {
        onDrop(acceptedFiles);
      }
      setFilesForUpload(acceptedFiles);
      if (url) {
        submitForm();
      }
    },
    ...options,
  });

  return (
    <Container {...getRootProps({ isDragActive, isDragAccept, isDragReject })} style={style}>
      <input {...getInputProps()} />
      {children || (
        <>
          <Text>{labels?.text || t('kit:dropzone.text')}</Text>
          <Text>{labels?.subtext || t('kit:dropzone.subtext')}</Text>
          <ButtonWrapper>
            <KitButton as='div'>{labels?.button || t('kit:dropzone.uploadFiles')}</KitButton>
          </ButtonWrapper>
        </>
      )}
    </Container>
  );
};

export default KitDropzone;
